import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { API_BASE_URL } from '../config/apiBaseUrl';
import { getUnifiedApiErrorMessage } from '../utils/apiErrorMessage';

// API 기본 설정
const API_CONFIG = {
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
    'X-API-Version': 'v1'
  },
  withCredentials: true // 쿠키 사용을 위해 true로 설정
};

// API 응답 타입 (백엔드 UnifiedApiResponse와 호환)
export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  code?: string;
  /** 필드별 상세, violations 배열 등 — 사용자 메시지는 보통 최상위 `message` */
  error?: unknown;
  timestamp?: string;
}

// 인증 관련 타입
export interface LoginRequest {
  username: string;
  password: string;
  deviceInfo?: string;
}

export interface RegisterRequest {
  username: string;
  password: string;
  email: string;
  nickname: string;
  phone?: string;
}

export interface AuthResponse {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  user: {
    id: number;
    username: string;
    name?: string;
    nickname: string;
    email: string;
    role: string;
    status: string;
    emailVerified: boolean;
    phoneVerified: boolean;
    lastLoginAt: string;
  };
}

// WebAuthn 관련 타입
export interface WebAuthnRegisterStartRequest {
  username: string;
  email: string;
  name: string; // 실제 이름
  nickname: string;
  phone?: string;
  password?: string; // 비밀번호 (선택적)
  deviceName?: string;
}

export interface WebAuthnRegisterStartResponse {
  challenge: string;
  rp: {
    id: string;
    name: string;
  };
  user: {
    id: string;
    name: string;
    displayName: string;
  };
  pubKeyCredParams: Array<{
    type: string;
    alg: number;
  }>;
  timeout: number;
  attestation: string;
  authenticatorSelection: {
    authenticatorAttachment?: string;
    userVerification: string;
    residentKey: string;
  };
}

export interface WebAuthnRegisterFinishRequest {
  challenge: string;
  credential: {
    id: string;
    rawId: string;
    response: {
      clientDataJSON: string;
      attestationObject: string;
      transports?: string[];
    };
    type: string;
    clientExtensionResults?: Record<string, any>;
    authenticatorAttachment?: string;
  };
}

export interface WebAuthnLoginStartRequest {
  username: string;
}

export interface WebAuthnLoginStartResponse {
  challenge: string;
  timeout: number;
  rpId: string;
  allowCredentials: Array<{
    id: string;
    type: string;
    transports?: string[];
  }>;
  userVerification: string;
}

export interface WebAuthnLoginFinishRequest {
  challenge: string;
  credential: {
    id: string;
    rawId: string;
    response: {
      clientDataJSON: string;
      authenticatorData: string;
      signature: string;
      userHandle?: string;
    };
    type: string;
    clientExtensionResults?: Record<string, any>;
  };
  deviceInfo?: string;
}

// 자산 유형
export type AssetType = 'STOCK' | 'CURRENCY';

// 투자 기록 관련 타입
export interface InvestmentRecord {
  id: number;
  recordDate: string;
  type: 'BUY' | 'SELL';
  assetType?: AssetType; // 자산 유형 (기본값: STOCK)
  stockName: string;
  stockCode?: string;
  // 외환거래 필드
  currencyPair?: string; // 통화쌍 (예: USD/KRW, EUR/USD)
  baseCurrency?: string; // 기준 통화 (예: USD)
  quoteCurrency?: string; // 상대 통화 (예: KRW)
  exchangeRate?: number; // 환율
  investmentRatio: number;
  quantity?: number;
  pricePerShare?: number;
  totalAmount?: number;
  dividendPerShare?: number;
  dividendDate?: string;
  dividendRatio?: number;
  buyReason?: string;
  sellReason?: string;
  sellQuantity?: number;
  sellRatio?: number;
  sellPrice?: number; // 매도 단가 (주당 가격)
  realizedProfitRate?: number;
  realizedProfitAmount?: number;
  selectedStockId?: number;
  currentPrice?: number;
  unrealizedProfitRate?: number;
  unrealizedProfitAmount?: number;
  isDeleted: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface BuyInvestmentRequest {
  recordDate: string;
  assetType?: AssetType; // 자산 유형 (기본값: STOCK)
  stockName: string;
  stockCode?: string;
  // 외환거래 필드
  currencyPair?: string;
  baseCurrency?: string;
  quoteCurrency?: string;
  exchangeRate?: number;
  investmentRatio: number;
  quantity?: number;
  pricePerShare?: number;
  dividendPerShare?: number;
  buyReason?: string;
}

export interface SellInvestmentRequest {
  recordDate: string;
  selectedStockId: number;
  sellQuantity?: number;
  sellRatio?: number;
  realizedProfitRate?: number;
  sellPrice?: number; // 매도 단가 (주당 가격)
  sellReason?: string;
}

export interface UpdateInvestmentRequest {
  id: number;
  assetType?: AssetType;
  stockName?: string;
  stockCode?: string;
  currencyPair?: string;
  baseCurrency?: string;
  quoteCurrency?: string;
  exchangeRate?: number;
  investmentRatio?: number;
  quantity?: number;
  pricePerShare?: number;
  totalAmount?: number;
  buyReason?: string;
  sellReason?: string;
  sellQuantity?: number;
  sellRatio?: number;
  sellPrice?: number; // 매도 단가 (주당 가격)
  realizedProfitRate?: number;
  dividendPerShare?: number;
}

export interface DeleteInvestmentRequest {
  id: number;
}

// 포트폴리오 관련 타입
export interface PortfolioSummary {
  totalSeed: number;
  totalInvestment: number;
  totalProfitRate: number;
  totalDividendRate: number;
  totalProfitAmount: number;
  totalDividendAmount: number;
  dividendYear?: number; // 배당 기준 연도
  lastUpdated: string;
}

export interface PortfolioSettings {
  totalSeed: number;
  currency: string;
  createdAt: string;
  updatedAt: string;
}

// 캘린더 관련 타입
export interface CalendarDay {
  date: string;
  profitRate?: number; // 기존 필드 (호환성 유지)
  profitRateChange?: number; // 전날 대비 평단가 기준 수익률 변화량
  isHoliday?: boolean; // 공휴일 여부
  canCalculate?: boolean; // 수익률 변동치 계산 가능 여부
  hasInvestment: boolean;
  recordCount: number;
  totalInvestment: number;
}

export interface DailyPortfolioStock {
  stockCode: string;
  stockName: string;
  holdingQuantity: number;
  averagePrice: number;
  currentPrice?: number; // 현재 종가 (현지 통화 기준)
  currency?: string; // 통화 (KRW, USD 등)
  exchangeRate?: number; // 환율 (현지 통화 -> 원화)
  previousProfitRate: number;
  todayProfitRate: number;
  profitRateChange: number;
  profitAmount: number;
}

export interface DailyPortfolioResponse {
  date: string;
  stocks: DailyPortfolioStock[];
}

export interface CalendarResponse {
  year: number;
  month: number;
  days: CalendarDay[];
}

// 자산관리 관련 타입
export interface AssetSettings {
  id?: number;
  startDate: string; // yyyy-MM-dd
  initialBalance: number;
  savings?: number; // 월 저축액
  existingSavings?: number; // 기존 저축액
  retirementPension?: number; // 퇴직연금
  investmentSeed?: number; // 투자시드
  createdAt?: string;
  updatedAt?: string;
}

export interface MonthlyActualBalance {
  id?: number;
  year: number;
  month: number;
  actualBalance?: number;
  difference?: number; // 계산된 금액과 실제 금액의 차이
  investmentSeedAddition?: number; // 해당 월 투자시드 증액 (순수 현금에서 차감)
  createdAt?: string;
  updatedAt?: string;
}

export interface FixedIncome {
  id: number;
  userId: number;
  name: string;
  amount: number;
  day: number; // 매월 며칠에 받는지 (1-31)
  startDate?: string; // 시작 날짜 (null이면 계산 시작 날짜부터)
  endDate?: string; // 종료 날짜 (null이면 무제한)
  createdAt?: string;
  updatedAt?: string;
}

export interface FixedExpense {
  id: number;
  userId: number;
  name: string;
  amount: number;
  day: number; // 매월 며칠에 지출하는지 (1-31)
  startDate?: string; // 시작 날짜 (null이면 계산 시작 날짜부터)
  endDate?: string; // 종료 날짜 (null이면 무제한)
  createdAt?: string;
  updatedAt?: string;
}

export interface PeriodIncome {
  id: number;
  userId: number;
  name: string;
  amount: number;
  startDate: string; // yyyy-MM-dd
  endDate: string; // yyyy-MM-dd
  createdAt?: string;
  updatedAt?: string;
}

export interface PeriodExpense {
  id: number;
  userId: number;
  name: string;
  amount: number;
  startDate: string; // yyyy-MM-dd
  endDate: string; // yyyy-MM-dd
  createdAt?: string;
  updatedAt?: string;
}

// API 서비스 클래스
class ApiService {
  public api: AxiosInstance;

  constructor() {
    this.api = axios.create(API_CONFIG);
    this.setupInterceptors();
  }

  // 인터셉터 설정
  private setupInterceptors() {
    // 요청 인터셉터 - 쿠키는 자동으로 전송되므로 별도 처리 불필요
    this.api.interceptors.request.use(
      (config) => {
        // 쿠키는 withCredentials: true로 자동 전송됨
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // 응답 인터셉터 (토큰 갱신)
    this.api.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;
          
          try {
            // 쿠키에서 refresh token이 자동으로 전송됨
            const response = await axios.post(`${API_CONFIG.baseURL}/auth/refresh`, {}, {
              withCredentials: true
            });
            
            // 새로운 토큰은 쿠키에 자동으로 설정됨
            return this.api(originalRequest);
          } catch (refreshError) {
            // 리프레시 토큰도 만료된 경우 로그아웃
            this.logout();
            window.location.href = '/login';
            return Promise.reject(refreshError);
          }
        }

        const data = error.response?.data;
        if (data != null && typeof data === 'object') {
          (error as { normalizedMessage?: string }).normalizedMessage = getUnifiedApiErrorMessage(data);
        }

        return Promise.reject(error);
      }
    );
  }

  // 인증 관련 메서드
  async register(userData: RegisterRequest): Promise<ApiResponse<any>> {
    const response = await this.api.post('/auth/register', userData);
    return response.data;
  }

  /** 문의하기 (비로그인 가능, DB 저장) */
  async submitContactInquiry(payload: {
    name: string;
    email: string;
    subject: string;
    message: string;
  }): Promise<ApiResponse<{ id: number }>> {
    const response = await this.api.post('/contact/inquiries', payload);
    return response.data;
  }

  async login(credentials: LoginRequest): Promise<ApiResponse<AuthResponse>> {
    const response = await this.api.post('/auth/login', credentials);
    // 토큰은 쿠키에 자동으로 저장됨 (백엔드에서 설정)
    return response.data;
  }

  async logout(): Promise<ApiResponse<any>> {
    try {
      await this.api.post('/auth/logout');
      // 쿠키는 백엔드에서 자동으로 삭제됨
    } catch (error) {
      console.error('로그아웃 요청 실패:', error);
    }
    
    return { 
      success: true, 
      data: null, 
      message: '로그아웃되었습니다.',
      timestamp: new Date().toISOString()
    };
  }

  async refreshToken(): Promise<ApiResponse<AuthResponse>> {
    // 쿠키에서 refresh token이 자동으로 전송됨
    const response = await this.api.post('/auth/refresh', {});
    return response.data;
  }

  // WebAuthn 관련 메서드
  async webauthnRegisterStart(request: WebAuthnRegisterStartRequest): Promise<ApiResponse<WebAuthnRegisterStartResponse>> {
    const response = await this.api.post('/auth/webauthn/register/start', request);
    return response.data;
  }

  async webauthnRegisterFinish(request: WebAuthnRegisterFinishRequest): Promise<ApiResponse<any>> {
    const response = await this.api.post('/auth/webauthn/register/finish', request);
    return response.data;
  }

  async webauthnLoginStart(request: WebAuthnLoginStartRequest): Promise<ApiResponse<WebAuthnLoginStartResponse>> {
    const response = await this.api.post('/auth/webauthn/login/start', request);
    return response.data;
  }

  async webauthnLoginFinish(request: WebAuthnLoginFinishRequest): Promise<ApiResponse<AuthResponse>> {
    const response = await this.api.post('/auth/webauthn/login/finish', request);
    // 토큰은 쿠키에 자동으로 저장됨 (백엔드에서 설정)
    return response.data;
  }

  // CORS 테스트
  async testCors(): Promise<ApiResponse<any>> {
    try {
      const response = await this.api.get('/cors-test/ping');
      return response.data;
    } catch (error: any) {
      console.error('CORS 테스트 실패:', error);
      throw error;
    }
  }

  // CORS POST 테스트
  async testCorsPost(data: any): Promise<ApiResponse<any>> {
    try {
      const response = await this.api.post('/cors-test/echo', data);
      return response.data;
    } catch (error: any) {
      console.error('CORS POST 테스트 실패:', error);
      throw error;
    }
  }

  // 투자 기록 관련 메서드
  async getInvestments(params: {
    page?: number;
    size?: number;
    type?: string;
    startDate?: string;
    endDate?: string;
  } = {}): Promise<ApiResponse<{
    content: InvestmentRecord[];
    totalElements: number;
    totalPages: number;
    currentPage: number;
    size: number;
  }>> {
    const response = await this.api.get('/investments', { params });
    return response.data;
  }

  async getInvestment(id: number): Promise<ApiResponse<InvestmentRecord>> {
    const response = await this.api.get(`/investments/${id}`);
    return response.data;
  }

  async createBuyInvestment(data: BuyInvestmentRequest): Promise<ApiResponse<InvestmentRecord>> {
    const response = await this.api.post('/investments/buy', data);
    return response.data;
  }

  async createSellInvestment(data: SellInvestmentRequest): Promise<ApiResponse<InvestmentRecord>> {
    const response = await this.api.post('/investments/sell', data);
    return response.data;
  }

  async updateInvestment(data: UpdateInvestmentRequest): Promise<ApiResponse<InvestmentRecord>> {
    const response = await this.api.post('/investments/update', data);
    return response.data;
  }

  async deleteInvestment(id: number): Promise<ApiResponse<any>> {
    const response = await this.api.post('/investments/delete', { id });
    return response.data;
  }

  // 포트폴리오 관련 메서드
  async getPortfolioSummary(): Promise<ApiResponse<PortfolioSummary>> {
    const response = await this.api.get('/portfolio/summary');
    return response.data;
  }

  async getPortfolioSettings(): Promise<ApiResponse<PortfolioSettings>> {
    const response = await this.api.get('/portfolio/settings');
    return response.data;
  }

  async updatePortfolioSettings(data: {
    totalSeed: number;
    currency: string;
  }): Promise<ApiResponse<PortfolioSettings>> {
    const response = await this.api.post('/portfolio/settings/update', data);
    return response.data;
  }

  // 캘린더 관련 메서드
  async getCalendarData(year: number, month: number): Promise<ApiResponse<CalendarResponse>> {
    const response = await this.api.get('/calendar', {
      params: { year, month }
    });
    return response.data;
  }
  
  async getDailyPortfolio(date: string): Promise<ApiResponse<DailyPortfolioResponse>> {
    const response = await this.api.get(`/calendar/${date}/portfolio`);
    return response.data;
  }

  async getCalendarRecords(date: string): Promise<ApiResponse<InvestmentRecord[]>> {
    const response = await this.api.get(`/calendar/${date}/records`);
    return response.data;
  }

  // 자산관리 관련 메서드
  async getAssetSettings(): Promise<ApiResponse<AssetSettings>> {
    const response = await this.api.get('/asset/settings');
    return response.data;
  }

  async saveAssetSettings(startDate: string, initialBalance: number, savings?: number, existingSavings?: number, retirementPension?: number, investmentSeed?: number): Promise<ApiResponse<AssetSettings>> {
    const params: any = { startDate, initialBalance };
    // savings가 undefined나 null이 아닐 때만 추가 (0도 유효한 값이므로 포함)
    if (savings !== undefined && savings !== null) {
      params.savings = savings.toString();
    }
    // existingSavings가 undefined나 null이 아닐 때만 추가 (0도 유효한 값이므로 포함)
    if (existingSavings !== undefined && existingSavings !== null) {
      params.existingSavings = existingSavings.toString();
    }
    // retirementPension이 undefined나 null이 아닐 때만 추가 (0도 유효한 값이므로 포함)
    if (retirementPension !== undefined && retirementPension !== null) {
      params.retirementPension = retirementPension.toString();
    }
    // investmentSeed가 undefined나 null이 아닐 때만 추가 (0도 유효한 값이므로 포함)
    if (investmentSeed !== undefined && investmentSeed !== null) {
      params.investmentSeed = investmentSeed.toString();
    }
    const response = await this.api.post('/asset/settings', null, { params });
    return response.data;
  }
  
  async getFixedIncomes(): Promise<ApiResponse<FixedIncome[]>> {
    const response = await this.api.get('/asset/fixed-incomes');
    return response.data;
  }
  
  async createFixedIncome(name: string, amount: number, day: number, startDate?: string, endDate?: string): Promise<ApiResponse<FixedIncome>> {
    const params: any = { name, amount, day };
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    const response = await this.api.post('/asset/fixed-incomes', null, { params });
    return response.data;
  }
  
  async updateFixedIncome(id: number, name: string, amount: number, day: number, startDate?: string, endDate?: string): Promise<ApiResponse<FixedIncome>> {
    const params: any = { name, amount, day };
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    const response = await this.api.post(`/asset/fixed-incomes/${id}/update`, null, { params });
    return response.data;
  }
  
  async deleteFixedIncome(id: number): Promise<ApiResponse<void>> {
    const response = await this.api.post(`/asset/fixed-incomes/${id}/delete`);
    return response.data;
  }
  
  async getFixedExpenses(): Promise<ApiResponse<FixedExpense[]>> {
    const response = await this.api.get('/asset/fixed-expenses');
    return response.data;
  }
  
  async createFixedExpense(name: string, amount: number, day: number, startDate?: string, endDate?: string): Promise<ApiResponse<FixedExpense>> {
    const params: any = { name, amount, day };
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    const response = await this.api.post('/asset/fixed-expenses', null, { params });
    return response.data;
  }
  
  async updateFixedExpense(id: number, name: string, amount: number, day: number, startDate?: string, endDate?: string): Promise<ApiResponse<FixedExpense>> {
    const params: any = { name, amount, day };
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    const response = await this.api.post(`/asset/fixed-expenses/${id}/update`, null, { params });
    return response.data;
  }
  
  async deleteFixedExpense(id: number): Promise<ApiResponse<void>> {
    const response = await this.api.post(`/asset/fixed-expenses/${id}/delete`);
    return response.data;
  }
  
  async getPeriodIncomes(): Promise<ApiResponse<PeriodIncome[]>> {
    const response = await this.api.get('/asset/period-incomes');
    return response.data;
  }
  
  async createPeriodIncome(name: string, amount: number, startDate: string, endDate: string): Promise<ApiResponse<PeriodIncome>> {
    const response = await this.api.post('/asset/period-incomes', null, {
      params: { name, amount, startDate, endDate }
    });
    return response.data;
  }
  
  async deletePeriodIncome(id: number): Promise<ApiResponse<void>> {
    const response = await this.api.post(`/asset/period-incomes/${id}/delete`);
    return response.data;
  }
  
  async getPeriodExpenses(): Promise<ApiResponse<PeriodExpense[]>> {
    const response = await this.api.get('/asset/period-expenses');
    return response.data;
  }
  
  async createPeriodExpense(name: string, amount: number, startDate: string, endDate: string): Promise<ApiResponse<PeriodExpense>> {
    const response = await this.api.post('/asset/period-expenses', null, {
      params: { name, amount, startDate, endDate }
    });
    return response.data;
  }
  
  async deletePeriodExpense(id: number): Promise<ApiResponse<void>> {
    const response = await this.api.post(`/asset/period-expenses/${id}/delete`);
    return response.data;
  }

  async getMonthlyActualBalance(year: number, month: number): Promise<ApiResponse<MonthlyActualBalance>> {
    const response = await this.api.get(`/asset/monthly-balance/${year}/${month}`);
    return response.data;
  }

  async getMonthlyActualBalancesByYear(year: number): Promise<ApiResponse<MonthlyActualBalance[]>> {
    const response = await this.api.get(`/asset/monthly-balance/${year}`);
    return response.data;
  }

  async saveMonthlyActualBalance(
    year: number, 
    month: number, 
    actualBalance: number, 
    calculatedBalance?: number
  ): Promise<ApiResponse<MonthlyActualBalance>> {
    const response = await this.api.post('/asset/monthly-balance', null, {
      params: { year, month, actualBalance, calculatedBalance }
    });
    return response.data;
  }

  async deleteMonthlyActualBalance(
    year: number,
    month: number
  ): Promise<ApiResponse<void>> {
    const response = await this.api.post('/asset/monthly-balance/delete', null, {
      params: { year, month }
    });
    return response.data;
  }

  async saveMonthlyInvestmentSeedAddition(
    year: number,
    month: number,
    amount?: number
  ): Promise<ApiResponse<MonthlyActualBalance>> {
    const response = await this.api.post('/asset/monthly-investment-seed-addition', null, {
      params: { year, month, amount: amount != null ? amount : undefined }
    });
    return response.data;
  }

  // 유틸리티 메서드
  // 쿠키는 JavaScript에서 직접 읽을 수 없으므로, 
  // 인증 상태는 백엔드 API 호출 결과나 로그인 응답의 user 정보로 판단
  isAuthenticated(): boolean {
    // 쿠키 기반이므로 항상 true 반환 (실제 인증은 백엔드에서 검증)
    // 또는 로그인 응답에서 받은 user 정보가 있는지 확인
    // 여기서는 간단히 true 반환 (실제로는 user 정보 저장 여부로 판단)
    return true; // 쿠키가 있으면 자동으로 인증됨
  }

  // Base64 URL 디코딩 (한글 지원) - 제거 (쿠키에서 토큰을 읽을 수 없음)
  private base64UrlDecode(str: string): string {
    // Base64 URL을 일반 Base64로 변환
    let base64 = str.replace(/-/g, '+').replace(/_/g, '/');
    // 패딩 추가
    while (base64.length % 4) {
      base64 += '=';
    }
    // Base64 디코딩
    const binaryString = atob(base64);
    // UTF-8 디코딩
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return new TextDecoder('utf-8').decode(bytes);
  }

  /**
   * 현재 로그인한 사용자 정보 조회
   * 쿠키에서 토큰을 읽어 서버에서 사용자 정보를 가져옴
   */
  async getCurrentUser(): Promise<ApiResponse<any>> {
    try {
      const response = await this.api.get('/auth/me');
      return response.data;
    } catch (error: any) {
      console.error('사용자 정보 조회 실패:', error);
      throw error;
    }
  }

  /**
   * 사용자 프로필 정보 업데이트
   */
  async updateUserProfile(updates: {
    name?: string;
    nickname?: string;
    email?: string;
    phone?: string;
  }): Promise<ApiResponse<any>> {
    const response = await this.api.post('/auth/user/profile/update', updates);
    return response.data;
  }

  /**
   * 비밀번호 변경
   */
  async changePassword(data: {
    currentPassword: string;
    newPassword: string;
  }): Promise<ApiResponse<any>> {
    const response = await this.api.post('/auth/user/password/update', data);
    return response.data;
  }
}

// 싱글톤 인스턴스 생성
export const apiService = new ApiService();
export default apiService;
